import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import AdminShell from "./AdminShell";
import { AssigneeEditor } from "../components/AssigneeChip";
import EmailPreviewModal from "../components/EmailPreviewModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Per-submission workflow. Numbered steps guide the team from report → payment → chat,
 * with a Files side-panel that pushes admin uploads straight into the homeowner's portal.
 * State is derived from timestamps that already live on the submission doc, so the page
 * always resumes exactly where the team left off.
 */
export default function AdminSubmissionWorkflow() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [row, setRow] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(false);

  const reload = async () => {
    setReloading(true);
    try {
      const [rowRes, tierRes] = await Promise.all([
        axios.get(`${API}/survey/responses/${id}`, { withCredentials: true }),
        axios.get(`${API}/admin/pricing/tiers`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setRow(rowRes.data);
      setTiers((tierRes.data || []).filter((t) => t.is_active !== false));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load this submission.");
    } finally {
      setReloading(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) {
    return (
      <AdminShell title="Loading…">
        <div style={{ background: "var(--folk-card)", border: "1.5px solid var(--folk-border)", borderRadius: 18, padding: 24, color: "var(--folk-muted)" }}>
          Fetching this homeowner…
        </div>
      </AdminShell>
    );
  }

  if (!row) {
    return (
      <AdminShell title="Not found">
        <div style={{ background: "var(--folk-card)", border: "1.5px solid var(--folk-border)", borderRadius: 18, padding: 24 }}>
          We couldn't find that submission. <Link to="/admin">← back to submissions</Link>
        </div>
      </AdminShell>
    );
  }

  const name = row.contact_name || "Anonymous homeowner";
  const persona = row.persona_summary;
  const assessment = row.style_assessment;

  const workflow = deriveWorkflow(row);

  const right = (
    <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
      <button
        type="button"
        onClick={reload}
        data-testid="workflow-refresh"
        disabled={reloading}
        className="folk-pill"
        style={{ padding: "8px 14px", fontSize: 13, border: "1.5px solid var(--folk-border)", background: "transparent" }}
      >
        {reloading ? "Refreshing…" : "Refresh"}
      </button>
      <a
        href={`/portal/${row.id}`}
        target="_blank"
        rel="noreferrer"
        data-testid="workflow-open-portal"
        className="folk-pill"
        style={{ textDecoration: "none", padding: "8px 14px", fontSize: 13, background: "#FFF", color: "#68280E", border: "1.5px solid #68280E" }}
      >
        Preview as homeowner ↗
      </a>
      <Link
        to="/admin"
        data-testid="workflow-back"
        className="folk-pill"
        style={{ textDecoration: "none", padding: "8px 14px", fontSize: 13, background: "#68280E", color: "#FFF", border: "1.5px solid #68280E" }}
      >
        ← All submissions
      </Link>
    </div>
  );

  return (
    <AdminShell
      eyebrow={`Submission · ${row.id.slice(0, 8)}`}
      title={name.toLowerCase()}
      right={right}
    >
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 340px", gap: 32, alignItems: "flex-start" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
          <SummaryCard row={row} persona={persona} assessment={assessment} workflow={workflow} onAssigneesChange={reload} />
          <StepReport row={row} tiers={tiers} onChange={reload} />
          <StepPayment row={row} tiers={tiers} onChange={reload} />
          <StepLockInStyle row={row} onChange={reload} />
          <StepChat row={row} onChange={reload} />
        </div>

        <div style={{ position: "sticky", top: 20, display: "flex", flexDirection: "column", gap: 16 }}>
          <FilesPanel row={row} onChange={reload} />
        </div>
      </div>
    </AdminShell>
  );
}

/* ─────────── summary ─────────── */

function deriveWorkflow(row) {
  return {
    reportSentAt: row.report_ready_email_sent_at,
    pricingSentAt: row.pricing_email_sent_at,
    advancePaid: !!row.advance_paid,
    paidTier: row.paid_tier,
    queriesUnread: 0, // filled inside StepChat
  };
}

function StatusChip({ ok, label, testId }) {
  return (
    <span
      data-testid={testId}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "5px 10px", borderRadius: 999, fontSize: 12,
        background: ok ? "#F1F8EF" : "#F3ECE9",
        color: ok ? "#1C4F2C" : "#68280E",
        border: `1.5px solid ${ok ? "rgba(28, 79, 44, 0.35)" : "rgba(104, 40, 14, 0.3)"}`,
        fontFamily: "Host Grotesk, sans-serif",
      }}
    >
      <span aria-hidden style={{ fontWeight: 700 }}>{ok ? "✓" : "○"}</span>
      {label}
    </span>
  );
}

function SummaryCard({ row, persona, assessment, workflow, onAssigneesChange }) {
  const answersCount = Object.keys(row.answers || {}).length;
  return (
    <div
      data-testid="workflow-summary"
      style={{ background: "var(--folk-card)", border: "1.5px solid var(--folk-border)", borderRadius: 18, padding: 24 }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 12, alignItems: "flex-start" }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--folk-hint)", fontWeight: 600 }}>Contact</div>
          <div style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 15.5, color: "#2A2828", marginTop: 4 }}>
            {row.contact_email || <span style={{ color: "var(--folk-hint)" }}>No email captured</span>}
          </div>
          <div style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 13, color: "var(--folk-muted)", marginTop: 4 }}>
            {answersCount} answer{answersCount === 1 ? "" : "s"} · submitted {new Date(row.submitted_at).toLocaleDateString()}
          </div>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "flex-end" }}>
          <StatusChip ok={!!workflow.reportSentAt} label="Report sent" testId="chip-report" />
          <StatusChip ok={!!workflow.pricingSentAt} label="Pricing sent" testId="chip-pricing" />
          <StatusChip ok={workflow.advancePaid} label="Advance paid" testId="chip-advance" />
        </div>
      </div>
      <div style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--folk-hint)", fontWeight: 700 }}>Owner</span>
        <AssigneeEditor
          submissionId={row.id}
          value={row.assignees || []}
          onChanged={onAssigneesChange}
        />
      </div>
      {persona && (
        <div style={{ marginTop: 16, padding: 16, borderRadius: 12, background: "#FFFBC9", border: "1.5px solid rgba(104, 40, 14, 0.18)", fontFamily: "'Playfair Display', Georgia, serif", fontSize: 14.5, lineHeight: 1.55, color: "#1B1A1A", maxHeight: 130, overflow: "hidden", position: "relative" }}>
          <div style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 10.5, letterSpacing: "0.14em", textTransform: "uppercase", color: "#68280E", fontWeight: 700, marginBottom: 6 }}>Persona (preview)</div>
          {persona.slice(0, 380)}{persona.length > 380 ? "…" : ""}
        </div>
      )}
      {assessment?.keywords?.length > 0 && (
        <div style={{ marginTop: 14, display: "flex", flexWrap: "wrap", gap: 6 }}>
          {assessment.keywords.slice(0, 8).map((k, i) => (
            <span key={i} style={{ fontSize: 11, padding: "3px 9px", border: "1.5px solid #0F3A54", color: "#0F3A54", borderRadius: 999, fontFamily: "Host Grotesk, sans-serif" }}>{k.toLowerCase()}</span>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─────────── shared step wrapper ─────────── */

function StepCard({ index, title, blurb, done, children, testId }) {
  return (
    <section
      data-testid={testId}
      style={{
        background: "var(--folk-card)",
        border: `1.5px solid ${done ? "rgba(28, 79, 44, 0.35)" : "var(--folk-border)"}`,
        borderRadius: 20, padding: 24,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
        <div style={{
          width: 32, height: 32, borderRadius: 999,
          background: done ? "#1C4F2C" : "#68280E", color: "#FFF",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontFamily: "Host Grotesk, sans-serif", fontWeight: 700, fontSize: 14,
          flexShrink: 0,
        }}>{done ? "✓" : index}</div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontFamily: "'Playfair Display', Georgia, serif", letterSpacing: "-0.028em", fontSize: 22, textTransform: "lowercase", lineHeight: 1.2 }}>{title.toLowerCase()}</div>
          {blurb && <div style={{ fontSize: 13, color: "var(--folk-muted)", marginTop: 3, fontFamily: "Host Grotesk, sans-serif" }}>{blurb}</div>}
        </div>
      </div>
      <div style={{ marginTop: 8 }}>{children}</div>
    </section>
  );
}

/* ─────────── step 1 — Report ─────────── */

function StepReport({ row, tiers, onChange }) {
  const [regenerating, setRegenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [briefBusy, setBriefBusy] = useState(false);
  const [preview, setPreview] = useState(false);
  const [genBoardBusy, setGenBoardBusy] = useState(false);

  const regenerate = async () => {
    setRegenerating(true);
    try {
      await axios.post(`${API}/survey/responses/${row.id}/regenerate-summary`, {}, { withCredentials: true });
      toast.success("Persona regenerated.");
      await onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Regenerate failed.");
    } finally { setRegenerating(false); }
  };

  const doSend = async () => {
    setSending(true);
    try {
      const res = await axios.post(`${API}/survey/responses/${row.id}/send-report-ready`, {}, { withCredentials: true });
      const pins = res.data?.pin_count ?? 0;
      toast.success(pins ? `Report-ready email sent with ${pins} pin${pins === 1 ? "" : "s"}.` : "Report-ready email sent.");
      if (res.data?.email_id === null) toast.warning("Resend not configured — email logged, not delivered.");
      await onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed.");
    } finally { setSending(false); }
  };

  const downloadBrief = async () => {
    setBriefBusy(true);
    try {
      const res = await axios.post(`${API}/survey/responses/${row.id}/moodboard-brief`, {}, { withCredentials: true, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const safe = (row.contact_name || "homeowner").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
      const a = document.createElement("a");
      a.href = url; a.download = `folk-moodboard-brief-${safe}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success("Moodboard brief ready — check downloads.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't build the brief.");
    } finally { setBriefBusy(false); }
  };

  const generateMoodboard = async () => {
    const url = window.prompt(
      "Paste a public Pinterest board URL to layer its pins in (optional). Leave blank for auto-curated only.",
      ""
    );
    // Cancel returns null; empty string is still a valid "auto-only" choice.
    if (url === null) return;
    const trimmed = (url || "").trim();
    setGenBoardBusy(true);
    try {
      const res = await axios.post(
        `${API}/admin/submissions/${row.id}/generate-moodboard`,
        trimmed ? { pinterest_url: trimmed } : {},
        { withCredentials: true }
      );
      const p = res.data?.pins_added ?? 0;
      const name = res.data?.board_name || "Moodboard";
      const sc = res.data?.source_counts || {};
      const boardPins = sc["pinterest-board"] || 0;
      const suffix = trimmed
        ? ` · ${boardPins} from board URL, ${p - (res.data?.internal_pins || 0) - boardPins} from search`
        : "";
      toast.success(`${name} · ${p} pin${p === 1 ? "" : "s"} added${suffix}.`);
      await onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't build the moodboard.");
    } finally { setGenBoardBusy(false); }
  };

  const done = !!row.report_ready_email_sent_at;

  return (
    <StepCard
      index={1}
      title="Send the report"
      blurb="Persona summary, style assessment and (if curated) moodboard pins land in the homeowner's inbox and portal."
      done={done}
      testId="step-report"
    >
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <button
          type="button"
          data-testid="step-report-regenerate"
          onClick={regenerate}
          disabled={regenerating}
          className="folk-pill"
          style={{ padding: "10px 16px", fontSize: 13, background: "transparent", border: "1.5px solid var(--folk-accent)", color: "var(--folk-accent)" }}
        >
          {regenerating ? "Regenerating…" : row.persona_summary ? "Regenerate persona" : "Generate persona"}
        </button>
        <button
          type="button"
          data-testid="step-report-download-brief"
          onClick={downloadBrief}
          disabled={briefBusy}
          className="folk-pill"
          style={{ padding: "10px 16px", fontSize: 13, background: "#FFFBC9", color: "#0F3A54", border: "1.5px solid #0F3A54" }}
        >
          {briefBusy ? "Preparing…" : "Download moodboard brief PDF"}
        </button>
        <button
          type="button"
          data-testid="step-report-generate-moodboard"
          onClick={generateMoodboard}
          disabled={genBoardBusy}
          className="folk-pill"
          style={{ padding: "10px 16px", fontSize: 13, background: "#BADDF0", color: "#0F3A54", border: "1.5px solid #0F3A54" }}
          title={row.auto_moodboard_board_id
            ? "Build a fresh moodboard alongside the current one — existing pins stay untouched."
            : "Auto-build a moodboard from this homeowner's survey selections + Claude style assessment."}
        >
          {genBoardBusy
            ? "Building…"
            : row.auto_moodboard_board_id
              ? "Regenerate moodboard"
              : "Generate moodboard"}
        </button>
        <button
          type="button"
          data-testid="step-report-send-email"
          onClick={() => {
            if (!row.contact_email) return toast.error("No contact email on this submission.");
            setPreview(true);
          }}
          disabled={sending || !row.contact_email}
          className="folk-pill"
          style={{ padding: "10px 16px", fontSize: 13, background: "#68280E", color: "#FFFBC9", border: "1.5px solid #68280E", opacity: (sending || !row.contact_email) ? 0.55 : 1 }}
        >
          {sending ? "Sending…" : done ? "Resend report-ready email" : "Send report-ready email"}
        </button>
        {done && (
          <span style={{ fontSize: 12, color: "var(--folk-muted)", alignSelf: "center" }}>
            last sent {new Date(row.report_ready_email_sent_at).toLocaleString()}
          </span>
        )}
      </div>
      <div style={{ marginTop: 12, fontSize: 12.5, color: "var(--folk-muted)", fontFamily: "Host Grotesk, sans-serif" }}>
        The email attaches a persona PDF that also includes palette, textures, do's, don'ts and any homeowner photos.
      </div>
      {tiers.length > 0 && !row.persona_summary && (
        <div style={{ marginTop: 10, fontSize: 12.5, color: "#68280E" }}>
          Regenerate the persona first — the moodboard brief needs it.
        </div>
      )}
      <EmailPreviewModal
        open={preview}
        onClose={() => setPreview(false)}
        title="Send report-ready email?"
        recipient={row.contact_email}
        subject={`your Folk report is live, ${(row.contact_name || "friend").split(" ")[0]}`}
        bodyPreview={[
          `Hey ${(row.contact_name || "there").split(" ")[0]},`,
          "your homeowner report is ready. tap through to see the persona brief we wrote for you, the palette and textures we're carrying forward, and a curated moodboard.",
          "the pdf attached mirrors what's in the portal, so you can share it with family or your partner.",
        ]}
        attachments={["persona brief PDF (palette + do's/don'ts + your photos)"]}
        onSend={doSend}
        confirmLabel="Send now"
      />
    </StepCard>
  );
}

/* ─────────── step 2 — Payment ─────────── */

function StepPayment({ row, tiers, onChange }) {
  const [savingTier, setSavingTier] = useState(false);
  const [sending, setSending] = useState(false);
  const [simBusy, setSimBusy] = useState(false);
  const [recommendedTierId, setRecommendedTierId] = useState(row.recommended_tier_id || "");
  const [preview, setPreview] = useState(false);

  useEffect(() => { setRecommendedTierId(row.recommended_tier_id || ""); }, [row.recommended_tier_id]);

  const saveTier = async (nextId) => {
    const prev = recommendedTierId;
    setRecommendedTierId(nextId);
    setSavingTier(true);
    try {
      await axios.put(`${API}/survey/responses/${row.id}/report-config`, { recommended_tier_id: nextId || null }, { withCredentials: true });
      toast.success("Recommendation saved.");
      await onChange();
    } catch (e) {
      setRecommendedTierId(prev);
      toast.error(e.response?.data?.detail || "Couldn't save recommendation.");
    } finally { setSavingTier(false); }
  };

  const doSendPricing = async () => {
    setSending(true);
    try {
      const res = await axios.post(`${API}/survey/responses/${row.id}/send-pricing`, {}, { withCredentials: true });
      toast.success("Report + pricing email sent.");
      if (res.data?.email_id === null) toast.warning("Resend not configured — email logged.");
      await onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed.");
    } finally { setSending(false); }
  };

  const openPreview = () => {
    if (!row.contact_email) return toast.error("No contact email on this submission.");
    setPreview(true);
  };

  const simulatePaid = async () => {
    if (!recommendedTierId) return toast.error("Pick a recommended plan first — simulate needs a tier.");
    setSimBusy(true);
    try {
      await axios.post(`${API}/survey/responses/${row.id}/simulate-payment`, { tier_id: recommendedTierId }, { withCredentials: true });
      toast.success("Marked as paid — kickstart triggered.");
      await onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't mark as paid.");
    } finally { setSimBusy(false); }
  };

  const done = !!row.pricing_email_sent_at;

  return (
    <StepCard
      index={2}
      title="Send payment link"
      blurb="Pick the plan you're recommending, then send the pricing email. Simulate the advance until Razorpay is live."
      done={done}
      testId="step-payment"
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <label style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--folk-hint)", fontWeight: 600 }}>Recommended plan</label>
        <select
          data-testid="step-payment-tier"
          value={recommendedTierId}
          onChange={(e) => saveTier(e.target.value)}
          disabled={savingTier}
          className="folk-input"
          style={{ maxWidth: 380 }}
        >
          <option value="">No recommendation</option>
          {tiers.map((t) => (
            <option key={t.id} value={t.id}>{t.name} · {t.price_display}</option>
          ))}
        </select>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 6 }}>
          <button
            type="button"
            data-testid="step-payment-send"
            onClick={openPreview}
            disabled={sending || !row.contact_email}
            className="folk-pill"
            style={{ padding: "10px 16px", fontSize: 13, background: "#68280E", color: "#FFFBC9", border: "1.5px solid #68280E", opacity: (sending || !row.contact_email) ? 0.55 : 1 }}
          >
            {sending ? "Sending…" : done ? "Resend pricing email" : "Send pricing email"}
          </button>
          <button
            type="button"
            data-testid="step-payment-simulate"
            onClick={simulatePaid}
            disabled={simBusy || row.advance_paid}
            className="folk-pill"
            style={{ padding: "10px 16px", fontSize: 13, background: row.advance_paid ? "#F1F8EF" : "transparent", color: row.advance_paid ? "#1C4F2C" : "#0F3A54", border: `1.5px solid ${row.advance_paid ? "rgba(28, 79, 44, 0.35)" : "#0F3A54"}` }}
          >
            {row.advance_paid ? "Advance recorded ✓" : simBusy ? "Marking…" : "Simulate advance paid"}
          </button>
          {done && (
            <span style={{ fontSize: 12, color: "var(--folk-muted)", alignSelf: "center" }}>
              last sent {new Date(row.pricing_email_sent_at).toLocaleString()}
            </span>
          )}
        </div>
      </div>
      <EmailPreviewModal
        open={preview}
        onClose={() => setPreview(false)}
        title="Send pricing email?"
        recipient={row.contact_email}
        subject={`your Folk report and pricing, ${(row.contact_name || "friend").split(" ")[0]}`}
        bodyPreview={[
          `Hey ${(row.contact_name || "there").split(" ")[0]},`,
          "here's your persona report along with the plan we're recommending and how to lock in your advance.",
          recommendedTierId
            ? `Recommended plan: ${(tiers.find((t) => t.id === recommendedTierId)?.name) || "selected tier"}.`
            : "No plan is selected yet, the email will still send but won't highlight one.",
        ]}
        attachments={["report + rate card PDF"]}
        onSend={doSendPricing}
        confirmLabel="Send now"
      />
      <ProductBillingTracker row={row} tiers={tiers} onChange={onChange} />
    </StepCard>
  );
}

/* ─────────── product-billing tracker (drives the 10% rebate badge) ─────────── */

function ProductBillingTracker({ row, tiers, onChange }) {
  const activeTier = tiers.find((t) => t.id === row.recommended_tier_id);
  const eligible = !!activeTier && /full|turnkey/i.test(activeTier.name || "");
  const [amount, setAmount] = useState(String(row.product_billing_total ?? 0));
  const [saving, setSaving] = useState(false);
  useEffect(() => { setAmount(String(row.product_billing_total ?? 0)); }, [row.product_billing_total]);

  const inr = (n) => "₹" + (Number(n) || 0).toLocaleString("en-IN");
  const threshold = 500000;
  const current = Number(amount) || 0;
  const progress = Math.max(0, Math.min(100, (current / threshold) * 100));
  const active = eligible && current >= threshold;

  const save = async () => {
    if (!eligible) return toast.error("This tier isn't rebate-eligible — Folk Full or Turnkey only.");
    setSaving(true);
    try {
      await axios.put(`${API}/survey/responses/${row.id}/product-billing`, { product_billing_total: current }, { withCredentials: true });
      toast.success(active ? "Rebate unlocked on the homeowner's portal." : "Product billing updated.");
      await onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save.");
    } finally { setSaving(false); }
  };

  if (!eligible) {
    return (
      <div style={{ marginTop: 18, padding: "14px 16px", background: "#FAF5F3", border: "1.5px dashed var(--folk-border)", borderRadius: 12, fontFamily: "Host Grotesk, sans-serif", fontSize: 13, color: "var(--folk-muted)" }}>
        Product‑billing rebate tracker is available on <strong>Folk Full</strong> and <strong>Turnkey</strong> plans only. Pick a qualifying plan above to enable it.
      </div>
    );
  }

  return (
    <div
      data-testid="product-billing-tracker"
      style={{
        marginTop: 18,
        padding: "16px 18px",
        background: active ? "#EAF3ED" : "#F5F8FB",
        border: `1.5px solid ${active ? "rgba(28, 79, 44, 0.35)" : "rgba(15, 58, 84, 0.24)"}`,
        borderRadius: 14,
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--folk-hint)", fontWeight: 700 }}>Product billing to date</span>
        {active && (
          <span data-testid="admin-rebate-active-chip" style={{ fontSize: 10.5, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 800, padding: "3px 9px", borderRadius: 999, background: "#1C4F2C", color: "#EAF3ED" }}>rebate unlocked</span>
        )}
      </div>

      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <span style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 14, color: "var(--folk-muted)" }}>₹</span>
        <input
          data-testid="product-billing-input"
          type="number"
          min="0"
          step="1000"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="folk-input"
          style={{ maxWidth: 200, padding: "8px 12px", fontFamily: "Host Grotesk, sans-serif", fontSize: 14 }}
        />
        <button
          type="button"
          data-testid="product-billing-save"
          onClick={save}
          disabled={saving}
          className="folk-pill"
          style={{ padding: "8px 16px", fontSize: 13, background: "#0F3A54", color: "#FFFBC9", border: "1.5px solid #0F3A54" }}
        >
          {saving ? "Saving…" : "Save"}
        </button>
        <span style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 12.5, color: "var(--folk-muted)" }}>
          {inr(current)} of {inr(threshold)} threshold · {active ? `10% rebate = ${inr(current * 0.10)}` : `${(100 - progress).toFixed(0)}% to unlock`}
        </span>
      </div>

      <div style={{ position: "relative", height: 6, background: "rgba(15, 58, 84, 0.12)", borderRadius: 4, overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, width: `${progress}%`, background: active ? "#1C4F2C" : "#0F3A54", borderRadius: 4, transition: "width 300ms ease" }} />
      </div>
    </div>
  );
}

/* ─────────── step 3 — Lock in the style (recommended board + hero pins) ─────────── */

function StepLockInStyle({ row, onChange }) {
  const [boards, setBoards] = useState([]);
  const [pins, setPins] = useState([]);
  const [role, setRole] = useState(null);
  const [busy, setBusy] = useState(false);
  const [savingBoardId, setSavingBoardId] = useState(null);
  const [savingPinId, setSavingPinId] = useState(null);

  const load = React.useCallback(async () => {
    setBusy(true);
    try {
      const res = await axios.get(`${API}/portal/${row.id}/moodboards`, { withCredentials: true });
      const bs = res.data?.boards || [];
      setBoards(bs);
      setRole(res.data?.role || null);
      // Flatten all pins so the admin can hero any across every board.
      setPins(bs.flatMap((b) => (b.pins || []).map((p) => ({ ...p, board_name: b.name }))));
    } catch (e) {
      /* silent */
    } finally { setBusy(false); }
  }, [row.id]);

  useEffect(() => { load(); }, [load]);

  const heroCount = pins.filter((p) => p.is_hero).length;
  const recommendedId = (boards.find((b) => b.is_recommended) || {}).id || "";

  const setRecommended = async (boardId) => {
    setSavingBoardId(boardId || "clear");
    try {
      await axios.put(`${API}/admin/submissions/${row.id}/recommended-board`, { board_id: boardId || null }, { withCredentials: true });
      toast.success(boardId ? "Recommended board locked in. Homeowner will only see this board." : "Recommendation cleared.");
      await load();
      await onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save.");
    } finally { setSavingBoardId(null); }
  };

  const toggleHero = async (pin) => {
    setSavingPinId(pin.id);
    try {
      const nextHero = !pin.is_hero;
      await axios.put(`${API}/admin/submissions/${row.id}/pins/${pin.id}/hero`, { is_hero: nextHero }, { withCredentials: true });
      setPins((prev) => prev.map((p) => p.id === pin.id ? { ...p, is_hero: nextHero } : p));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't update hero pin.");
    } finally { setSavingPinId(null); }
  };

  const done = !!recommendedId;
  const backend = BACKEND_URL || "";

  const noBoards = !busy && boards.length === 0;

  return (
    <StepCard
      index={3}
      title="Lock in the style"
      blurb="Pick the moodboard that becomes the homeowner's style direction, and star up to 12 hero pins that lead their report."
      done={done}
      testId="step-lock-style"
    >
      {noBoards ? (
        <div style={{ padding: 18, background: "#FAF5F3", border: "1.5px dashed var(--folk-border)", borderRadius: 12, fontFamily: "Host Grotesk, sans-serif", fontSize: 14, color: "var(--folk-muted)" }}>
          No moodboards yet. Generate one from Step 1 first, then come back here to lock in the style.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

          <div>
            <div style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--folk-hint)", fontWeight: 700, marginBottom: 10 }}>Recommended board</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {boards.map((b) => {
                const isRec = b.is_recommended;
                const saving = savingBoardId === b.id;
                return (
                  <button
                    key={b.id}
                    type="button"
                    data-testid={`lock-board-${b.id}`}
                    onClick={() => setRecommended(b.id)}
                    disabled={saving}
                    className="folk-pill"
                    style={{
                      padding: "8px 14px",
                      fontSize: 13,
                      background: isRec ? "#1C4F2C" : "transparent",
                      color: isRec ? "#EAF3ED" : "#2A2828",
                      border: `1.5px solid ${isRec ? "#1C4F2C" : "var(--folk-border)"}`,
                      opacity: saving ? 0.6 : 1,
                    }}
                  >
                    {isRec ? "✓ " : ""}{b.name} <span style={{ opacity: 0.7, marginLeft: 6 }}>{(b.pins || []).length}</span>
                  </button>
                );
              })}
              {recommendedId && (
                <button
                  type="button"
                  data-testid="lock-board-clear"
                  onClick={() => setRecommended(null)}
                  disabled={savingBoardId === "clear"}
                  className="folk-pill"
                  style={{ padding: "8px 14px", fontSize: 13, background: "transparent", color: "var(--folk-muted)", border: "1.5px solid var(--folk-border)" }}
                >
                  Clear recommendation
                </button>
              )}
            </div>
          </div>

          <div>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, marginBottom: 10 }}>
              <div style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--folk-hint)", fontWeight: 700 }}>Hero pins</div>
              <div data-testid="lock-hero-count" style={{ fontSize: 12.5, color: heroCount >= 12 ? "#B04A2E" : "var(--folk-muted)", fontWeight: heroCount >= 12 ? 700 : 400 }}>
                {heroCount} / 12 starred
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 10 }}>
              {pins.map((p) => {
                const src = p.image_url?.startsWith("http") ? p.image_url : `${backend}${p.image_url}`;
                const saving = savingPinId === p.id;
                const disabled = saving || (!p.is_hero && heroCount >= 12);
                return (
                  <button
                    key={p.id}
                    type="button"
                    data-testid={`lock-hero-${p.id}`}
                    onClick={() => toggleHero(p)}
                    disabled={disabled}
                    title={p.is_hero ? "Unstar (remove hero)" : (heroCount >= 12 ? "Max 12 hero pins" : "Star as hero")}
                    style={{
                      position: "relative", padding: 0, borderRadius: 12, overflow: "hidden",
                      border: `2px solid ${p.is_hero ? "#F0B458" : "transparent"}`,
                      cursor: disabled ? "not-allowed" : "pointer",
                      opacity: disabled && !p.is_hero ? 0.5 : 1,
                      background: "#EFE5E1",
                    }}
                  >
                    <img src={src} alt="" loading="lazy" style={{ width: "100%", display: "block", aspectRatio: "3 / 4", objectFit: "cover" }} />
                    <div style={{
                      position: "absolute", top: 6, right: 6,
                      background: p.is_hero ? "#F0B458" : "rgba(0,0,0,0.55)",
                      color: p.is_hero ? "#3E2A1E" : "#FFF",
                      borderRadius: 999, padding: "3px 8px",
                      fontSize: 12, fontWeight: 800,
                      fontFamily: "Host Grotesk, sans-serif",
                    }}>
                      {p.is_hero ? "★" : "☆"}
                    </div>
                  </button>
                );
              })}
            </div>
            {pins.length === 0 && !busy && (
              <div style={{ color: "var(--folk-muted)", fontSize: 13.5 }}>No pins on this submission's boards yet.</div>
            )}
          </div>

        </div>
      )}
    </StepCard>
  );
}

/* ─────────── step 4 — Chat ─────────── */


function StepChat({ row, onChange }) {
  const [tickets, setTickets] = useState([]);
  const [busy, setBusy] = useState(false);
  const [replies, setReplies] = useState({});
  const [sendingId, setSendingId] = useState(null);

  const loadTickets = async () => {
    setBusy(true);
    try {
      const res = await axios.get(`${API}/portal/${row.id}/queries`, { withCredentials: true });
      setTickets(Array.isArray(res.data) ? res.data : []);
    } catch { /* silent */ }
    finally { setBusy(false); }
  };

  useEffect(() => { loadTickets(); /* eslint-disable-next-line */ }, [row.id]);

  const send = async (queryId) => {
    const text = (replies[queryId] || "").trim();
    if (!text) return;
    setSendingId(queryId);
    try {
      await axios.post(`${API}/admin/queries/${queryId}/reply`, { reply: text }, { withCredentials: true });
      setReplies((r) => ({ ...r, [queryId]: "" }));
      await loadTickets();
      await onChange();
      toast.success("Reply sent.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send reply.");
    } finally { setSendingId(null); }
  };

  const openCount = tickets.filter((t) => t.status === "open").length;
  const done = tickets.length > 0 && openCount === 0;

  return (
    <StepCard
      index={4}
      title="Enquiries & chat"
      blurb="Homeowner leaves a message, you reply once from here. Reply also emails them via Resend."
      done={done}
      testId="step-chat"
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {busy && tickets.length === 0 ? (
          <div style={{ color: "var(--folk-muted)", fontSize: 13.5 }}>Loading messages…</div>
        ) : tickets.length === 0 ? (
          <div style={{ color: "var(--folk-muted)", fontSize: 13.5, fontStyle: "italic" }}>No messages yet from this homeowner.</div>
        ) : (
          <div data-testid="step-chat-thread" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {tickets.map((t) => {
              const isOpen = t.status === "open";
              return (
                <div
                  key={t.id}
                  data-testid={`chat-ticket-${t.id}`}
                  style={{
                    background: isOpen ? "#FFFBC9" : "var(--folk-bg)",
                    border: `1.5px solid ${isOpen ? "rgba(104, 40, 14, 0.25)" : "var(--folk-border)"}`,
                    borderRadius: 14, padding: 14,
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
                    <div style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 13.5, fontWeight: 600, color: "#2A2828" }}>
                      {t.subject || "(no subject)"}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--folk-muted)" }}>{new Date(t.created_at).toLocaleString()}</div>
                  </div>
                  <div style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 13.5, color: "#2A2828", marginTop: 6, whiteSpace: "pre-wrap", lineHeight: 1.5 }}>{t.message}</div>
                  {isOpen ? (
                    <div style={{ marginTop: 10, display: "flex", gap: 8, alignItems: "flex-end" }}>
                      <textarea
                        data-testid={`chat-reply-${t.id}`}
                        value={replies[t.id] || ""}
                        onChange={(e) => setReplies((r) => ({ ...r, [t.id]: e.target.value }))}
                        placeholder="Write a reply — this emails them too."
                        className="folk-input"
                        style={{ flex: 1, minHeight: 60, resize: "vertical", fontFamily: "Host Grotesk, sans-serif", fontSize: 13.5 }}
                      />
                      <button
                        type="button"
                        data-testid={`chat-send-${t.id}`}
                        onClick={() => send(t.id)}
                        disabled={sendingId === t.id || !(replies[t.id] || "").trim()}
                        className="folk-pill"
                        style={{ padding: "8px 14px", fontSize: 12.5, background: "#68280E", color: "#FFF", border: "1.5px solid #68280E", opacity: (sendingId === t.id || !(replies[t.id] || "").trim()) ? 0.55 : 1 }}
                      >
                        {sendingId === t.id ? "Sending…" : "Reply"}
                      </button>
                    </div>
                  ) : (
                    <div style={{ marginTop: 10, padding: 10, background: "var(--folk-card)", border: "1px dashed var(--folk-border)", borderRadius: 10 }}>
                      <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--folk-hint)", fontWeight: 600, marginBottom: 4 }}>
                        Folk reply · {t.replied_at ? new Date(t.replied_at).toLocaleString() : ""}
                      </div>
                      <div style={{ fontSize: 13, color: "#2A2828", whiteSpace: "pre-wrap", lineHeight: 1.5 }}>{t.admin_reply}</div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </StepCard>
  );
}

/* ─────────── Files side panel ─────────── */

const FILE_KINDS = [
  { key: "home_layouts",      label: "Floor plans & layouts",   accept: "image/*,application/pdf",  maxMB: 25  },
  { key: "home_walkthroughs", label: "Photos & walkthroughs",   accept: "image/*,video/*",          maxMB: 100 },
  { key: "renders",           label: "Project renders",         accept: "image/*",                  maxMB: 25  },
  { key: "sketches",          label: "Sketches",                accept: "image/*,application/pdf",  maxMB: 25  },
  { key: "layouts",           label: "Layout library",          accept: "image/*,application/pdf",  maxMB: 25  },
  { key: "custom_furniture",  label: "Custom furniture",        accept: "image/*,application/pdf",  maxMB: 25  },
  { key: "materials_finishes", label: "Materials & finishes",   accept: "image/*,application/pdf",  maxMB: 25  },
  { key: "paint_textures",    label: "Paint & textures",        accept: "image/*",                  maxMB: 25  },
  { key: "upholstery",        label: "Upholstery",              accept: "image/*",                  maxMB: 25  },
];

function FilesPanel({ row, onChange }) {
  const [openKind, setOpenKind] = useState("home_layouts");
  return (
    <div data-testid="workflow-files-panel" style={{ background: "var(--folk-card)", border: "1.5px solid var(--folk-border)", borderRadius: 18, padding: 20 }}>
      <div style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--folk-accent)", fontWeight: 700 }}>Files in their portal</div>
      <div style={{ fontFamily: "'Playfair Display', Georgia, serif", letterSpacing: "-0.028em", fontSize: 22, textTransform: "lowercase", marginTop: 4, marginBottom: 14 }}>upload once, they see it.</div>

      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {FILE_KINDS.map((k) => {
          const items = (row[k.key] || []).filter((f) => !f.is_deleted);
          const open = openKind === k.key;
          return (
            <div key={k.key} style={{ borderTop: "1px dashed var(--folk-border)", paddingTop: 6 }}>
              <button
                type="button"
                data-testid={`files-toggle-${k.key}`}
                onClick={() => setOpenKind(open ? "" : k.key)}
                style={{
                  width: "100%", textAlign: "left",
                  background: "transparent", border: "none", cursor: "pointer",
                  padding: "6px 4px", fontFamily: "Host Grotesk, sans-serif",
                  display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8,
                }}
              >
                <span style={{ fontSize: 13.5, color: "#2A2828", fontWeight: open ? 600 : 400 }}>{k.label}</span>
                <span style={{ fontSize: 11.5, color: "var(--folk-muted)" }}>{items.length}</span>
              </button>
              {open && (
                <FileKindEditor
                  submissionId={row.id}
                  kind={k.key}
                  accept={k.accept}
                  maxMB={k.maxMB}
                  items={items}
                  onChange={onChange}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FileKindEditor({ submissionId, kind, accept, maxMB, items, onChange }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (files) => {
    if (!files || !files.length) return;
    setBusy(true);
    for (const file of Array.from(files)) {
      const form = new FormData();
      form.append("file", file);
      form.append("uploaded_by", "admin");
      try {
        await axios.post(`${API}/portal/${submissionId}/uploads/${kind}`, form, {
          headers: { "Content-Type": "multipart/form-data" },
          withCredentials: true,
        });
      } catch (e) {
        toast.error(e.response?.data?.detail || `Couldn't upload ${file.name}`);
      }
    }
    setBusy(false);
    if (inputRef.current) inputRef.current.value = "";
    await onChange();
    toast.success("Uploaded to their portal.");
  };

  const remove = async (fid) => {
    if (!window.confirm("Remove this file from the homeowner's portal?")) return;
    try {
      await axios.delete(`${API}/portal/${submissionId}/uploads/${kind}/${fid}`, { withCredentials: true });
      await onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed.");
    }
  };

  return (
    <div style={{ padding: "6px 2px 12px", display: "flex", flexDirection: "column", gap: 8 }}>
      <button
        type="button"
        data-testid={`files-upload-${kind}`}
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        style={{
          border: "1.5px dashed var(--folk-border)",
          background: "var(--folk-bg)", borderRadius: 12,
          padding: "10px 12px", cursor: "pointer",
          fontFamily: "Host Grotesk, sans-serif", fontSize: 12.5, color: "var(--folk-muted)",
        }}
      >
        {busy ? "Uploading…" : `+ upload (max ${maxMB}MB each)`}
      </button>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={accept}
        onChange={(e) => upload(e.target.files)}
        style={{ display: "none" }}
      />
      {items.length === 0 ? (
        <div style={{ fontSize: 12, color: "var(--folk-hint)", fontStyle: "italic" }}>Nothing here yet.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {items.map((f) => {
            const url = `${BACKEND_URL}/api/uploads/${f.path}`;
            const isVideo = (f.content_type || "").startsWith("video/");
            const isRender = kind === "renders";
            const status = f.match_status; // 'matching' | 'matched' | 'no_matches' | 'failed' | undefined
            const finalizeRender = async () => {
              try {
                await axios.post(`${API}/admin/submissions/${submissionId}/renders/${f.id}/finalize`, {}, { withCredentials: true });
                toast.success("Render marked final. Auto-match is running in the background…");
                await onChange();
              } catch (e) {
                toast.error(e.response?.data?.detail || "Could not finalize.");
              }
            };
            return (
              <div key={f.id} style={{ display: "flex", alignItems: "center", gap: 8, background: "var(--folk-bg)", border: "1px solid var(--folk-border)", borderRadius: 10, padding: "6px 10px", flexWrap: "wrap" }}>
                <a href={url} target="_blank" rel="noreferrer" style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: "var(--folk-accent)", textDecoration: "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {isVideo ? "▶ " : ""}{f.filename}
                </a>
                {isRender && status && (
                  <span
                    data-testid={`render-status-${f.id}`}
                    style={{
                      fontSize: 10.5, letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 700,
                      padding: "3px 8px", borderRadius: 999,
                      background: status === "matched" ? "#1C4F2C" : status === "matching" ? "#F0B458" : status === "no_matches" ? "#8A6B54" : "#B04A2E",
                      color: status === "matched" ? "#EAF3ED" : "#3E2A1E",
                    }}
                  >
                    {status === "matched" ? "matched ✓" : status === "matching" ? "matching…" : status === "no_matches" ? "no matches" : "failed"}
                  </span>
                )}
                {isRender && !f.is_final && (
                  <button
                    type="button"
                    data-testid={`render-finalize-${f.id}`}
                    onClick={finalizeRender}
                    className="folk-pill"
                    style={{ padding: "4px 10px", fontSize: 11.5, background: "#1C4F2C", color: "#EAF3ED", border: "1.5px solid #1C4F2C" }}
                  >
                    Mark as final
                  </button>
                )}
                {isRender && f.is_final && (
                  <button
                    type="button"
                    data-testid={`render-rerun-${f.id}`}
                    onClick={finalizeRender}
                    disabled={status === "matching"}
                    className="folk-pill"
                    style={{ padding: "4px 10px", fontSize: 11.5, background: "transparent", color: "var(--folk-muted)", border: "1.5px solid var(--folk-border)" }}
                  >
                    Re-run match
                  </button>
                )}
                <button
                  type="button"
                  data-testid={`files-remove-${f.id}`}
                  onClick={() => remove(f.id)}
                  style={{ background: "transparent", border: "none", color: "var(--folk-muted)", cursor: "pointer", fontSize: 11, textDecoration: "underline" }}
                >
                  remove
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
